import { Button, Card } from 'nhsuk-react-components';
import useUser from 'hooks/useUser.ts';
import { Iteration } from 'schemas/Iteration.schema.ts';
import { SummaryReport } from 'schemas/SummaryReport.schema.ts';
import {
  getSummaryReportActionButtons,
  getSummaryReportActionsForStatus,
  SummaryReportActionButton,
  SummaryReportActions,
} from 'utils/summaryReports';
import RejectSummaryReport from 'components/SummaryReportsActions/Reject';
import SendToNotify from 'components/SummaryReportsActions/SendToNotify';
import BlueBox from 'components/SummaryReport/BlueBox';
import ApproveSummaryReport from 'components/SummaryReportsActions/Approve';
import { CampaignConfig, IterationDetails } from 'schemas/CampaignConfig.schema.ts';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';
import 'components/SummaryReport/SummaryReport.scss';
import { conditionTargetToLabel } from 'utils/formattingUtils';

interface IReportDetailsProps {
  report: SummaryReport;
  iteration: Iteration;
  iterationDetails: IterationDetails;
  teamId: string;
  campaign: CampaignConfig;
  isCurrentSummaryReport: boolean;
}

function ReportDetails({
  iteration,
  iterationDetails,
  report,
  teamId,
  campaign,
  isCurrentSummaryReport,
}: IReportDetailsProps) {
  const user = useUser();
  const isCreatedByMe = iterationDetails.created_by.id === user.userId;
  const isWriter = user.roles.includes(`${teamId}:write`);
  const isApprover = (report.reviewers ?? []).includes(user.userId) && !isCreatedByMe;
  const isReleaser = user.roles.includes(`${teamId}:release`);
  const campaignId = campaign.CampaignConfig.ID!;
  const hasCommsTargets = report.summary_report_comms_routing.comms_routing.some((r) => r.comms_target);

  const availableSummaryReportActions = getSummaryReportActionsForStatus(
    isCurrentSummaryReport,
    isWriter,
    iterationDetails.status as ITERATION_STATUS,
  );

  const availableSummaryReportButtons = getSummaryReportActionButtons(
    report,
    availableSummaryReportActions,
    isApprover,
  );

  return (
    <>
      <Card className="summary-report">
        <>
          <h2>Totals</h2>

          <div className="blue-box-container nhsuk-grid-row nhsuk-u-margin-bottom-5">
            <div className="nhsuk-grid-column-one-third">
              <BlueBox
                title={report.summary_report_totals.totals.invited.toLocaleString()}
                text="Total invites"
              />
            </div>
            <div className="nhsuk-grid-column-one-third">
              <BlueBox
                title={report.summary_report_totals.totals.filtered.toLocaleString()}
                text="Total filtered"
              />
            </div>
            <div className="nhsuk-grid-column-one-third">
              <BlueBox
                title={report.summary_report_totals.totals.suppressed.toLocaleString()}
                text="Total suppressions"
              />
            </div>
          </div>

          <div className="nhsuk-u-margin-bottom-5">
            <table>
              <caption>
                <h2>Filters and suppressions</h2>
              </caption>
              <thead>
                <tr>
                  <th scope="col">Priority</th>
                  <th scope="col">Type</th>
                  <th scope="col">Rule name</th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Exclusions count
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Minimum age
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Maximum age
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.summary_report_exclusions.exclusions.map(
                  (exclusion) => (
                    <tr key={exclusion.rule_names}>
                      <td>{exclusion.priority}</td>
                      <td>
                        {exclusion.type === 'F' ? 'Filter' : 'Suppression'}
                      </td>
                      <td>{exclusion.rule_names}</td>
                      <td className="nhsuk-u-text-align-right">
                        {exclusion.exclusions_count}
                      </td>
                      <td className="nhsuk-u-text-align-right">
                        {exclusion.age_min !== 'nan'
                        && exclusion.age_max !== 'nan'
                          ? `${Math.round(Number.parseFloat(exclusion.age_min))}`
                          : 'N/A'}
                      </td>
                      <td className="nhsuk-u-text-align-right">
                        {exclusion.age_max !== 'nan'
                          ? `${Math.round(Number.parseFloat(exclusion.age_max))}`
                          : 'N/A'}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>

          <div className="table-container">
            <table>
              <caption>
                <h2>Cohorts</h2>
              </caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Priority
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Distinct comms routing
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Total
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Invited
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Invited other cohort
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Filtered
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Suppressed
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Excluded
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Age min
                  </th>
                  <th scope="col" className="nhsuk-u-text-align-right">
                    Age max
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.summary_report_counts.counts.map((count) => (
                  <tr key={count.cohort_label}>
                    <td className="word-wrap-cell">{count.cohort_label}</td>
                    <td className="nhsuk-u-text-align-right">
                      {count.priority}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.distinct_comms_routing}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.cohort_label_count}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.invited_count}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.invited_other_cohort}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.filtered_count}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.suppressed_count}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.excluded_count}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.min_age}
                    </td>
                    <td className="nhsuk-u-text-align-right">
                      {count.max_age}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-container">
            <table>
              <caption>
                <h2>Routing</h2>
              </caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  {hasCommsTargets && (
                    <th scope="col">Target</th>
                  )}
                  <th
                    scope="col"
                    className="nhsuk-u-text-align-right nhsuk-u-padding-right-6"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.summary_report_comms_routing.comms_routing.map(
                  (route) => (
                    <tr key={route.comms_routing}>
                      <td>
                        <div className="nhsuk-u-padding-bottom-3">{route.comms_routing}</div>
                        {route.comms_routing_plan_id && (
                          <>
                            <div>Routing plan ID:</div>
                            <div>{route.comms_routing_plan_id}</div>
                          </>
                        )}
                      </td>
                      {hasCommsTargets && (
                        <td>{conditionTargetToLabel(route.comms_target ?? '')}</td>
                      )}
                      <td className="nhsuk-u-text-align-right nhsuk-u-padding-right-6">
                        {route.count}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <div className="nhsuk-u-margin-bottom-5 table-container">
            <table className="icb-table">
              <caption>
                <h2>ICBs</h2>
              </caption>
              <thead>
                <tr>
                  <th scope="col">ICB</th>
                  <th
                    scope="col"
                    className="nhsuk-u-padding-right-6"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.summary_report_icb_split.icb_split.map(
                  (icb: { icb: string; count: string }) => (
                    <tr key={icb.icb}>
                      <td>{icb.icb}</td>
                      <td className="nhsuk-u-padding-right-6">
                        {icb.count}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          {availableSummaryReportActions.includes(SummaryReportActions.Send) && isReleaser && (
            <div className="nhsuk-grid-row">
              <div style={{ display: 'inline-block', float: 'right' }}>
                <SendToNotify
                  teamId={teamId}
                  campaignId={campaignId}
                  iterationId={iteration.ID!}
                  batchId={report.batch_id!}
                />
              </div>
            </div>
          )}
        </>
      </Card>
      <div style={{ display: 'flex', gap: '25px' }}>
        {availableSummaryReportButtons === SummaryReportActionButton.SELECT_REVIEWERS && (
          <Button
            href={`/${teamId}/campaign/${campaignId}/iteration/${iterationDetails.ID}/summary/reports/${report.batch_id}/reviewers`}
          >
            Set report reviewers
          </Button>
        )}
        {availableSummaryReportButtons === SummaryReportActionButton.APPROVE_REJECT && (
          <>
            <RejectSummaryReport
              teamId={teamId}
              campaignId={campaignId}
              iterationId={iteration.ID!}
              batchId={report.batch_id!}
            />
            { report.summary_report_totals.totals.invited !== 0 && (
            <ApproveSummaryReport
              teamId={teamId}
              campaignId={campaignId}
              iterationDetails={iterationDetails}
              batchId={report.batch_id!}
            />
            )}
          </>
        )}
        {availableSummaryReportButtons === SummaryReportActionButton.REJECT_ONLY && (
          <RejectSummaryReport
            teamId={teamId}
            campaignId={campaignId}
            iterationId={iteration.ID!}
            batchId={report.batch_id!}
          />
        )}
      </div>
    </>
  );
}

export default ReportDetails;
